import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Send, Loader2, Moon, BedDouble, Hotel } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import { ChevronsUpDown, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const childAgeOptions = [
  { value: "2", label: "2 yrs" },
  { value: "3", label: "3 yrs" },
  { value: "4", label: "4 yrs" },
  { value: "5", label: "5 yrs" },
  { value: "6", label: "6 yrs" },
  { value: "7", label: "7 yrs" },
  { value: "8", label: "8 yrs" },
  { value: "9", label: "9 yrs" },
  { value: "10", label: "10 yrs" },
  { value: "11", label: "11 yrs" }
];

const infantAgeOptions = [
  { value: "0", label: "Under 1 yr" },
  { value: "1", label: "1 yr" },
  { value: "2", label: "2 yrs" }
];

const RequestQuote = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(false);
  const [nights, setNights] = useState(0);
  const [adminTeam, setAdminTeam] = useState([]);
  const [agentStaff, setAgentStaff] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [agents, setAgents] = useState([]);
  const editId = new URLSearchParams(location.search).get('edit');
  const isEditing = Boolean(editId);
  const [editLoading, setEditLoading] = useState(isEditing);
  const [formData, setFormData] = useState({
    guest_name: "",
    trip_type: "domestic",
    destination: "",
    covered_cities: "",
    pickup_location: "",
    drop_location: "",
    travel_date: "",
    return_date: "",
    pax_adults: 2,
    no_of_rooms: 1,
    cwb: 0, // Child With Bed
    cnb: 0, // Child No Bed
    pax_infants: 0,
    cwb_ages: [],
    cnb_ages: [],
    infant_ages: [],
    budget: "",
    hotel_category: "3-star",
    meal_plan: "cp",
    hotel_options_count: 1,
    requirements: "",
    with_hotel: "yes",
    assigned_to: "",
    agent_id: ""
  });

  // Admin mode: load agents list so admin can pick which agent the quote is for.
  useEffect(() => {
    if (!isAdmin || !token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/agents`, { headers: { Authorization: `Bearer ${token}` } });
        setAgents(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* silent */ }
    })();
  }, [isAdmin, token]);

  // Fetch active destinations for dropdown
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/destinations`);
        setDestinations((res.data || []).filter(d => d.status === 'active'));
      } catch (e) { /* silent */ }
    };
    load();
  }, []);

  // If editing existing quote, prefill form & guard against locked quotes
  useEffect(() => {
    if (!isEditing || !token) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/quotes/${editId}`, { headers: { Authorization: `Bearer ${token}` } });
        const q = res.data || {};
        // Guard: admin already quoted → redirect
        if ((q.quote_amount || 0) > 0 || ['quoted', 'approved', 'booked', 'confirmed'].includes(q.status)) {
          toast.error("This quote can no longer be edited — admin has already quoted.");
          navigate('/agent/quotes');
          return;
        }
        const splitAges = (v) => typeof v === 'string' ? v.split(',').filter(Boolean) : (Array.isArray(v) ? v : []);
        setFormData({
          guest_name: q.guest_name || q.client_name || '',
          trip_type: q.trip_type || 'domestic',
          destination: q.destination || '',
          covered_cities: q.covered_cities || '',
          pickup_location: q.pickup_location || '',
          drop_location: q.drop_location || '',
          travel_date: q.travel_date || '',
          return_date: q.return_date || '',
          pax_adults: q.pax_adults ?? 2,
          no_of_rooms: q.no_of_rooms ?? 1,
          cwb: q.cwb ?? 0,
          cnb: q.cnb ?? 0,
          pax_infants: q.pax_infants ?? 0,
          cwb_ages: splitAges(q.cwb_ages),
          cnb_ages: splitAges(q.cnb_ages),
          infant_ages: splitAges(q.infant_ages),
          budget: q.budget || '',
          hotel_category: q.hotel_category || '3-star',
          meal_plan: q.meal_plan || 'cp',
          hotel_options_count: q.hotel_options_count || 1,
          requirements: q.requirements || '',
          with_hotel: q.with_hotel || 'yes',
          assigned_to: q.assigned_to || '',
        });
      } catch (err) {
        console.error("Failed to load quote for edit:", err);
        toast.error("Could not load quote for editing");
      } finally {
        setEditLoading(false);
      }
    };
    load();
  }, [isEditing, editId, token, navigate]);

  // Calculate nights when dates change
  useEffect(() => {
    if (formData.travel_date && formData.return_date) {
      const start = new Date(formData.travel_date);
      const end = new Date(formData.return_date);
      const diffTime = end - start;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setNights(diffDays > 0 ? diffDays : 0);
    } else {
      setNights(0);
    }
  }, [formData.travel_date, formData.return_date]);

  // Pre-fill from package if navigated from Packages page
  useEffect(() => {
    const pkg = location.state?.fromPackage;
    if (pkg) {
      setFormData(prev => ({
        ...prev,
        destination: pkg.destination || prev.destination,
        trip_type: pkg.trip_type || prev.trip_type,
        hotel_category: pkg.hotel_category || prev.hotel_category,
        requirements: pkg.package_name ? `Package: ${pkg.package_name}` : prev.requirements
      }));
    }
  }, [location.state]);


  // Fetch agent's own staff for assignment (agent mode only)
  useEffect(() => {
    if (isAdmin) return;
    const fetchAgentStaff = async () => {
      try {
        const res = await axios.get(`${API}/agent/staff`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgentStaff(res.data || []);
      } catch (err) {
        console.error("Error fetching agent staff:", err);
      }
    };
    fetchAgentStaff();
  }, [token, isAdmin]);

  // Update cwb_ages array when cwb changes
  useEffect(() => {
    const count = parseInt(formData.cwb) || 0;
    setFormData(prev => ({
      ...prev,
      cwb_ages: Array(count).fill("").map((_, i) => prev.cwb_ages[i] || "")
    }));
  }, [formData.cwb]);

  // Update cnb_ages array when cnb changes
  useEffect(() => {
    const count = parseInt(formData.cnb) || 0;
    setFormData(prev => ({
      ...prev,
      cnb_ages: Array(count).fill("").map((_, i) => prev.cnb_ages[i] || "")
    }));
  }, [formData.cnb]);

  // Update infant_ages array when pax_infants changes
  useEffect(() => {
    const count = parseInt(formData.pax_infants) || 0;
    setFormData(prev => ({
      ...prev,
      infant_ages: Array(count).fill("").map((_, i) => prev.infant_ages[i] || "")
    }));
  }, [formData.pax_infants]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "travel_date" && formData.return_date && value > formData.return_date) {
      setFormData(prev => ({ ...prev, [name]: value, return_date: "" }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCwbAgeChange = (index, value) => {
    setFormData(prev => {
      const newAges = [...prev.cwb_ages];
      newAges[index] = value;
      return { ...prev, cwb_ages: newAges };
    });
  };

  const handleCnbAgeChange = (index, value) => {
    setFormData(prev => {
      const newAges = [...prev.cnb_ages];
      newAges[index] = value;
      return { ...prev, cnb_ages: newAges };
    });
  };

  const handleInfantAgeChange = (index, value) => {
    setFormData(prev => {
      const newAges = [...prev.infant_ages];
      newAges[index] = value;
      return { ...prev, infant_ages: newAges };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.guest_name || !formData.destination || !formData.travel_date || !formData.return_date) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        client_name: formData.guest_name, // Map guest_name to client_name for backend
        pax_children: parseInt(formData.cwb) + parseInt(formData.cnb),
        cwb_ages: formData.cwb_ages.join(","),
        cnb_ages: formData.cnb_ages.join(","),
        infant_ages: formData.infant_ages.join(","),
        nights: nights
      };
      // Admin mode: route the quote to the chosen agent (or treat as Direct).
      if (isAdmin) {
        payload.agent_id = formData.agent_id || "DIRECT";
      }

      if (isEditing) {
        await axios.put(`${API}/quotes/${editId}/agent-request-update`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Quote updated successfully!");
      } else {
        await axios.post(`${API}/quotes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isAdmin ? "Quote created — open in Quote Builder to set pricing." : "Quote request submitted successfully!");
      }
      navigate(isAdmin ? "/admin/quotes" : "/agent/quotes");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error(error.response?.data?.detail || "Failed to submit quote request");
    } finally {
      setLoading(false);
    }
  };

  const getMinReturnDate = () => {
    if (!formData.travel_date) return "";
    const travelDate = new Date(formData.travel_date);
    travelDate.setDate(travelDate.getDate() + 1);
    return travelDate.toISOString().split('T')[0];
  };

  const totalChildren = (parseInt(formData.cwb) || 0) + (parseInt(formData.cnb) || 0);

  return (
    <DashboardLayout type={isAdmin ? "admin" : "agent"}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="request-quote">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{isEditing ? 'Edit Quote Request' : (isAdmin ? 'Create Quote for Agent' : 'Request a Quote')}</h1>
          <p className="text-gray-500">{isEditing ? 'Update the travel details below. Locked once admin sends the quote.' : (isAdmin ? 'Fill in the travel details. After creating, open the quote in Quote Builder to set pricing.' : 'Fill in the travel details to get a quote')}</p>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-800">Quote Details</CardTitle>
          </CardHeader>
          <CardContent>
            {editLoading ? (
              <div className="space-y-4 py-4" data-testid="edit-quote-loading">
                {Array.from({length:6}).map((_,i)=>(<div key={i} className="h-12 rounded-md bg-gray-100 animate-pulse" />))}
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
              {/* Admin-only: which agent is this quote for */}
              {isAdmin && !isEditing && (
                <div>
                  <Label className="text-gray-600 mb-1 block">Select Agent *</Label>
                  <Select
                    value={formData.agent_id || "DIRECT"}
                    onValueChange={(v) => setFormData({...formData, agent_id: v === "DIRECT" ? "" : v})}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="admin-quote-agent-select">
                      <SelectValue placeholder="Direct Client / Choose Agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DIRECT">Direct Client (no agent)</SelectItem>
                      {agents.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}{a.company_name ? ` — ${a.company_name}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Trip Type Selection */}
              <div>
                <Label className="text-gray-600 mb-3 block">Trip Type *</Label>
                <RadioGroup
                  value={formData.trip_type}
                  onValueChange={(value) => setFormData({...formData, trip_type: value, with_hotel: "yes", destination: ""})}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="domestic" id="domestic" className="border-slate-600 text-primary-500" />
                    <Label htmlFor="domestic" className="text-gray-800 cursor-pointer">Domestic</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="international" id="international" className="border-slate-600 text-primary-500" />
                    <Label htmlFor="international" className="text-gray-800 cursor-pointer">International</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Guest Name */}
              <div>
                <Label className="text-gray-600 mb-1 block">Guest Name *</Label>
                <Input
                  value={formData.guest_name}
                  onChange={(e) => setFormData({...formData, guest_name: e.target.value})}
                  className="bg-gray-50 border-gray-200"
                  placeholder="Enter guest name"
                  required
                  data-testid="guest-name-input"
                />
              </div>

              {/* With Hotel / Without Hotel - Only for International */}
              {formData.trip_type === "international" && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-gray-700 mb-3 block font-medium flex items-center gap-2">
                    <Hotel className="w-4 h-4 text-blue-600" />
                    Hotel Requirement
                  </Label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, with_hotel: "yes"})}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                        formData.with_hotel === "yes"
                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Hotel className={`w-5 h-5 ${formData.with_hotel === "yes" ? "text-emerald-600" : "text-gray-400"}`} />
                        <span className="font-medium">With Hotel</span>
                      </div>
                      <p className="text-xs mt-1 opacity-75">Include hotel accommodation</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, with_hotel: "no"})}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                        formData.with_hotel === "no"
                          ? "border-amber-500 bg-amber-50 text-amber-700"
                          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <BedDouble className={`w-5 h-5 ${formData.with_hotel === "no" ? "text-amber-600" : "text-gray-400"}`} />
                        <span className="font-medium">Without Hotel</span>
                      </div>
                      <p className="text-xs mt-1 opacity-75">Land package only</p>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-600">Destination{formData.trip_type === "international" ? "s" : ""} *</Label>
                  {(() => {
                    const filtered = destinations.filter(d => (formData.trip_type === "international" ? d.country === "International" : d.country !== "International"));
                    const selected = (formData.destination || "").split(",").map(s => s.trim()).filter(Boolean);
                    const toggle = (name) => {
                      const next = selected.includes(name) ? selected.filter(x => x !== name) : [...selected, name];
                      setFormData({...formData, destination: next.join(", ")});
                    };
                    const remove = (name) => setFormData({...formData, destination: selected.filter(x => x !== name).join(", ")});
                    return (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="w-full flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-left hover:bg-gray-100 min-h-[42px]"
                            data-testid="quote-destination"
                          >
                            {selected.length === 0 ? (
                              <span className="text-gray-400">Select {formData.trip_type === "international" ? "one or more International destinations" : "a Domestic destination"}</span>
                            ) : (
                              <div className="flex flex-wrap gap-1.5 items-center">
                                {selected.map(name => (
                                  <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-md">
                                    {name}
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => { e.stopPropagation(); remove(name); }}
                                      className="hover:bg-emerald-200 rounded-sm"
                                      data-testid={`destination-chip-remove-${name}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </span>
                                  </span>
                                ))}
                              </div>
                            )}
                            <ChevronsUpDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                          <div className="max-h-60 overflow-y-auto p-2">
                            {filtered.length === 0 ? (
                              <div className="p-3 text-sm text-gray-400 text-center">No destinations available</div>
                            ) : (
                              filtered.map(d => {
                                const checked = selected.includes(d.name);
                                return (
                                  <label
                                    key={d.id}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-emerald-50 cursor-pointer"
                                    data-testid={`destination-option-${d.name}`}
                                  >
                                    <Checkbox checked={checked} onCheckedChange={() => toggle(d.name)} />
                                    <span className="text-sm text-gray-800">{d.name}</span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          {selected.length > 0 && (
                            <div className="border-t border-gray-100 p-2 flex items-center justify-between">
                              <span className="text-[11px] text-gray-500">{selected.length} selected</span>
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, destination: ""})}
                                className="text-[11px] text-red-600 hover:underline"
                                data-testid="destination-clear-all"
                              >
                                Clear all
                              </button>
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-gray-600">Covered Cities</Label>
                  <Input
                    name="covered_cities"
                    value={formData.covered_cities}
                    onChange={handleChange}
                    placeholder="e.g., Munnar, Thekkady, Alleppey"
                    className="bg-gray-50 border-gray-200 text-gray-800"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-600">Pick Up Location</Label>
                  <Input
                    name="pickup_location"
                    value={formData.pickup_location}
                    onChange={handleChange}
                    placeholder="e.g., Cochin Airport"
                    className="bg-gray-50 border-gray-200 text-gray-800"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">Drop Location</Label>
                  <Input
                    name="drop_location"
                    value={formData.drop_location}
                    onChange={handleChange}
                    placeholder="e.g., Cochin Airport"
                    className="bg-gray-50 border-gray-200 text-gray-800"
                  />
                </div>
              </div>

              {/* Travel Dates with Nights Display */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <Label className="text-gray-600">Travel Date *</Label>
                  <Input
                    name="travel_date"
                    type="date"
                    value={formData.travel_date}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="quote-travel-date"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">Return Date *</Label>
                  <Input
                    name="return_date"
                    type="date"
                    value={formData.return_date}
                    onChange={handleChange}
                    min={getMinReturnDate()}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="quote-return-date"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">No. of Nights</Label>
                  <div className="flex items-center gap-2 h-10 px-3 bg-gray-50 border border-gray-200 rounded-md">
                    <Moon className="w-4 h-4 text-emerald-600" />
                    <span className="text-gray-800 font-semibold text-lg">{nights}</span>
                    <span className="text-gray-500 text-sm">nights</span>
                  </div>
                </div>
              </div>

              {/* Adults, Rooms and Budget */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <Label className="text-gray-600">No. of Adults *</Label>
                  <Input
                    name="pax_adults"
                    type="number"
                    min="1"
                    value={formData.pax_adults}
                    onChange={handleChange}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="quote-adults"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">No. of Rooms{formData.with_hotel === "no" ? "" : " *"}</Label>
                  <div className="relative">
                    <BedDouble className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      name="no_of_rooms"
                      type="number"
                      min="1"
                      value={formData.no_of_rooms}
                      onChange={handleChange}
                      required={formData.with_hotel !== "no"}
                      disabled={formData.with_hotel === "no"}
                      className="bg-gray-50 border-gray-200 text-gray-800 pl-10 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="quote-rooms"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600">Budget (Optional)</Label>
                  <Input
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="e.g., ₹2-3 lakhs"
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="quote-budget"
                  />
                </div>
              </div>

              {/* Hotel Category & Meal Plan */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                    <Label className="text-gray-600">Hotel Category</Label>
                    <Select 
                        value={formData.hotel_category} 
                        onValueChange={(value) => setFormData({...formData, hotel_category: value})}
                        disabled={formData.with_hotel === "no"}
                    >
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="hotel-category-select">
                        <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="budget">Budget</SelectItem>
                        <SelectItem value="3-star">3-Star</SelectItem>
                        <SelectItem value="3-star-deluxe">3-Star Deluxe</SelectItem>
                        <SelectItem value="4-star">4-Star</SelectItem>
                        <SelectItem value="4-star-deluxe">4-Star Deluxe</SelectItem>
                        <SelectItem value="5-star">5-Star</SelectItem>
                        <SelectItem value="5-star-luxury">5-Star Luxury</SelectItem>
                        <SelectItem value="luxury">Luxury Resort</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-gray-600">Meal Plan</Label>
                    <Select 
                        value={formData.meal_plan} 
                        onValueChange={(value) => setFormData({...formData, meal_plan: value})}
                        disabled={formData.with_hotel === "no"}
                    >
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="meal-plan-select">
                        <SelectValue placeholder="Select Meal Plan" />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="ep">EP (Room Only)</SelectItem>
                        <SelectItem value="cp">CP (Breakfast)</SelectItem>
                        <SelectItem value="map">MAP (Breakfast + Dinner)</SelectItem>
                        <SelectItem value="ap">AP (All Meals)</SelectItem>
                        <SelectItem value="ai">All Inclusive</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-gray-600">Hotel Options Required</Label>
                    <Select 
                        value={String(formData.hotel_options_count)} 
                        onValueChange={(value) => setFormData({...formData, hotel_options_count: parseInt(value)})}
                        disabled={formData.with_hotel === "no"}
                    >
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="hotel-options-count">
                        <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="1">1 Option</SelectItem>
                        <SelectItem value="2">2 Options</SelectItem>
                        <SelectItem value="3">3 Options</SelectItem>
                        <SelectItem value="4">4 Options</SelectItem>
                        <SelectItem value="5">5 Options</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
              </div>

              {/* Assign To Agent Staff */}
              {agentStaff.length > 0 && (
                <div>
                  <Label className="text-gray-600">Assign To Staff</Label>
                  <Select 
                    value={formData.assigned_to} 
                    onValueChange={(value) => setFormData({...formData, assigned_to: value})}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="assign-to-dropdown">
                      <SelectValue placeholder="Select staff member" />
                    </SelectTrigger>
                    <SelectContent>
                      {agentStaff.map(member => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name} ({member.designation || 'Staff'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">Assign this quote to one of your team members</p>
                </div>
              )}

              {/* CWB - Child With Bed Section */}
              <div className="p-4 bg-emerald-50 border border-green-500/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-green-400 font-semibold">Child With Bed (CWB)</Label>
                    <p className="text-gray-500 text-xs mt-1">Children 2-11 yrs requiring extra bed</p>
                  </div>
                  <Input
                    name="cwb"
                    type="number"
                    min="0"
                    max="10"
                    value={formData.cwb}
                    onChange={handleChange}
                    className="w-20 bg-gray-50 border-gray-200 text-gray-800 text-center"
                    data-testid="quote-cwb"
                  />
                </div>
                {formData.cwb > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Array.from({ length: parseInt(formData.cwb) }).map((_, index) => (
                      <div key={`cwb-${index}`}>
                        <Label className="text-gray-500 text-xs">CWB {index + 1} Age</Label>
                        <Select 
                          value={formData.cwb_ages[index] || ""} 
                          onValueChange={(value) => handleCwbAgeChange(index, value)}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800 h-9">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {childAgeOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* CNB - Child No Bed Section */}
              <div className="p-4 bg-blue-50 border border-blue-500/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-blue-400 font-semibold">Child No Bed (CNB)</Label>
                    <p className="text-gray-500 text-xs mt-1">Children 2-11 yrs sharing bed with adults</p>
                  </div>
                  <Input
                    name="cnb"
                    type="number"
                    min="0"
                    max="10"
                    value={formData.cnb}
                    onChange={handleChange}
                    className="w-20 bg-gray-50 border-gray-200 text-gray-800 text-center"
                    data-testid="quote-cnb"
                  />
                </div>
                {formData.cnb > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Array.from({ length: parseInt(formData.cnb) }).map((_, index) => (
                      <div key={`cnb-${index}`}>
                        <Label className="text-gray-500 text-xs">CNB {index + 1} Age</Label>
                        <Select 
                          value={formData.cnb_ages[index] || ""} 
                          onValueChange={(value) => handleCnbAgeChange(index, value)}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800 h-9">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {childAgeOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Infants Section */}
              <div className="p-4 bg-purple-50 border border-purple-500/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-purple-400 font-semibold">Infants (0-2 yrs)</Label>
                    <p className="text-gray-500 text-xs mt-1">Infants under 2 years of age</p>
                  </div>
                  <Input
                    name="pax_infants"
                    type="number"
                    min="0"
                    max="10"
                    value={formData.pax_infants}
                    onChange={handleChange}
                    className="w-20 bg-gray-50 border-gray-200 text-gray-800 text-center"
                    data-testid="quote-infants"
                  />
                </div>
                {formData.pax_infants > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Array.from({ length: parseInt(formData.pax_infants) }).map((_, index) => (
                      <div key={`infant-${index}`}>
                        <Label className="text-gray-500 text-xs">Infant {index + 1} Age</Label>
                        <Select 
                          value={formData.infant_ages[index] || ""} 
                          onValueChange={(value) => handleInfantAgeChange(index, value)}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800 h-9">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {infantAgeOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Summary */}
              {(formData.pax_adults > 0 || totalChildren > 0 || formData.pax_infants > 0) && (
                <div className="p-4 bg-amber-50 border border-secondary-500/30 rounded-lg">
                  <Label className="text-secondary-400 font-semibold mb-2 block">Guest Summary</Label>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-gray-800">{formData.pax_adults} Adults</span>
                    {formData.cwb > 0 && <span className="text-green-400">{formData.cwb} CWB</span>}
                    {formData.cnb > 0 && <span className="text-blue-400">{formData.cnb} CNB</span>}
                    {formData.pax_infants > 0 && <span className="text-purple-400">{formData.pax_infants} Infants</span>}
                    <span className="text-gray-500">|</span>
                    <span className="text-gray-800">{formData.no_of_rooms} Rooms</span>
                    {nights > 0 && <span className="text-secondary-400">{nights} Nights</span>}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-gray-600">Special Requirements</Label>
                <Textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Any specific requirements, activities, dietary needs, etc."
                  className="bg-gray-50 border-gray-200 text-gray-800"
                  data-testid="quote-requirements"
                />
              </div>

              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-quote"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditing ? 'Saving…' : 'Submitting…'}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {isEditing ? 'Save Changes' : 'Submit Quote Request'}
                  </>
                )}
              </Button>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default RequestQuote;
